import { Field, Form, Formik } from 'formik';
import React, { useState, useEffect } from 'react';
import OrderSummary from '../orderSummary/OrderSummary';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import randomId from 'random-id';
import { toast } from 'react-hot-toast';
import { deleteApi, get, postApi } from '../../api';
import { clearCart } from '../../store/reducers/cartSlice';

const buildSchema = (usingSavedAddress) =>
	Yup.object().shape({
		firstName: Yup.string().required('First Name is required!'),
		lastName: Yup.string().required('Last name is required!'),
		email: Yup.string()
			.email('Invalid email')
			.required('Email address is required!'),
		phoneNumber: Yup.string().required('Phone number is required!'),
		address: usingSavedAddress
			? Yup.string()
			: Yup.string().required('Street address is required!'),
		city: Yup.string(),
	});

function Checkout() {
	let [isPayment, setIsPayment] = useState(false);
	let [order, setOrder] = useState('');
	let [isloading, setIsLoading] = useState(false);
	let [isError, setIsError] = useState(false);
	// Address book (#26): saved delivery addresses, selectable at checkout.
	const [addresses, setAddresses] = useState([]);
	const [selectedAddressId, setSelectedAddressId] = useState('new');
	const [saveNewAddress, setSaveNewAddress] = useState(false);
	const [newAddressLabel, setNewAddressLabel] = useState('');
	const dispatch = useDispatch();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		let cancelled = false;
		get('system/addresses/')
			.then((res) => {
				if (cancelled) return;
				const rows = res?.data?.results || [];
				setAddresses(rows);
				const preferred = rows.find((a) => a.is_default) || rows[0];
				setSelectedAddressId(preferred ? preferred.id : 'new');
			})
			.catch(() => {
				// Address book unavailable — checkout still works with manual entry.
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const removeAddress = async (id) => {
		try {
			await deleteApi(`system/addresses/${id}/`);
			const rest = addresses.filter((a) => a.id !== id);
			setAddresses(rest);
			if (selectedAddressId === id) {
				const preferred = rest.find((a) => a.is_default) || rest[0];
				setSelectedAddressId(preferred ? preferred.id : 'new');
			}
		} catch (error) {
			// deleteApi already toasts on failure.
		}
	};

	let navigate = useNavigate();
	var Id = randomId(30, 'aA0');
	const { cartTotalAmount } = useSelector((state) => state.cart);
	const { ...item } = useSelector((state) => state.cart);
	const { user } = useSelector((state) => state.user);
	const [shippingPrice, setShippingPrice] = useState('50');

	const onchangeSubmit = async (value) => {
		if (cartTotalAmount <= 50) {
			return toast.error("Order items must be worth more than $50");
		}

		const shippingPrice = value.shippingOption === "fedx" ? 50 : 60;

		const orderedItems = item.cartItems.map((cartItem) => ({
			quantity: cartItem.cartQuantity,
			inventory_item: cartItem.id || cartItem[0]?.id, // Adjust based on structure
		}));

		// Address book (#26): a selected saved address wins; otherwise the
		// typed address (+ city when given) is used and optionally saved.
		const savedAddress = addresses.find((a) => a.id === selectedAddressId);
		const shippingAddress = savedAddress
			? savedAddress.address
			: [value.address, value.city].filter(Boolean).join(', ');

		const orderData = {
			buyer_name: `${value.firstName} ${value.lastName}`,
			contact_number: value.phoneNumber,
			shipping_address: shippingAddress,
			// New orders land as `draft` ("New"); staff activate them to
			// processing, which releases the line items to sellers (#170).
			status: "draft",
			buyer: user?.user?.id || user?.user?._id, // Adjust based on user object
			ordered_items: orderedItems,
			paymentMethod: value.paymentMethod || "COD", // Ensure this field is set
		};

		if (orderData.paymentMethod === "COD") {
			try {
				setIsLoading(true); // Start loading state
				const res = await postApi(`sales/order-summaries/`, orderData);
				const data = res.data?.order_number;
				setIsLoading(false);
				toast.success("Order placed successfully!");

				if (!savedAddress && saveNewAddress && shippingAddress) {
					// Non-blocking: the order is already placed; a failed
					// address save should not disturb the flow.
					postApi('system/addresses/', {
						label: newAddressLabel.trim(),
						address: shippingAddress,
					}).catch(() => { });
				}

				dispatch(clearCart());
				// Uncomment and update navigation logic as needed
				navigate(`/order/${data}`, { state: { fromCheckout: true } });
			} catch (error) {
				const errorMessage =
					error?.response?.data?.error ||
					error?.response?.data?.message ||
					error?.response?.data?.error?.message ||
					error?.message;

				toast.error(errorMessage);
				setIsError(errorMessage);
				setTimeout(() => {
					setIsError(false);
				}, 2000);
				setIsLoading(false); // Reset loading state on error
			}
		} else {
			setIsPayment(true);
			setOrder(orderData); // Pass data to payment flow
		}
	};



	return (
		<div className="bg-gray-50">
			<div className="mx-auto max-w-screen-2xl px-3 sm:px-10">
				<div className="py-10 lg:py-12 px-0 2xl:max-w-screen-2xl w-full xl:max-w-screen-xl flex flex-col md:flex-row lg:flex-row">
					<div className="md:w-full lg:w-3/5 flex h-full flex-col order-2 sm:order-1 lg:order-1">
						<div className="mt-5 md:mt-0 md:col-span-2">
							<Formik
								initialValues={{
									firstName: user?.user?.given_name,
									lastName: user?.user?.family_name,
									email: user?.user?.email,
									phoneNumber: user?.user?.phone_number,
									address: user?.user?.address,
								}}
								validationSchema={buildSchema(selectedAddressId !== 'new')}
								onSubmit={(values, { setSubmitting, validateForm }) => {
									validateForm().then((errors) => {
										if (Object.keys(errors).length > 0) {
											setSubmitting(false);
											return;
										}
										onchangeSubmit(values);
										setSubmitting(false);
									});
								}}


							>
								{({ errors, touched, onchangeSubmit }) => {
									return (
										<Form>
											<div>
												<h2 className="font-semibold  text-base text-gray-700 pb-3">
													01. Personal Details
												</h2>
												<div className="grid grid-cols-6 gap-6">
													<div className="col-span-6 sm:col-span-3">
														<label
															htmlFor="firstName"
															className="block text-gray-500 font-medium text-sm leading-none mb-2"
														>
															First Name
														</label>
														<div className="relative">
															<Field
																className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg:white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
																id="firstName"
																name="firstName"
																placeholder="Samet"
															/>
														</div>
														{errors.firstName && touched.firstName && (
															<span className="text-red-400 text-sm mt-2">
																{errors.firstName}
															</span>
														)}
													</div>
													<div className="col-span-6 sm:col-span-3">
														<label
															htmlFor="lastName"
															className="block text-gray-500 font-medium text-sm leading-none mb-2"
														>
															Last Name
														</label>
														<div className="relative">
															<Field
																className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg:white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
																id="lastName"
																name="lastName"
																placeholder="Kaya"
															/>
														</div>
														{errors.lastName && touched.lastName && (
															<span className="text-red-400 text-sm mt-2">
																{errors.lastName}
															</span>
														)}
													</div>
													<div className="col-span-6 sm:col-span-3">
														<label
															htmlFor="email"
															className="block text-gray-500 font-medium text-sm leading-none mb-2"
														>
															Email Address
														</label>
														<div className="relative">
															<Field
																className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg:white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
																id="email"
																name="email"
																placeholder="info@gmail.com"
															/>
														</div>
														{errors.email && touched.email && (
															<span className="text-red-400 text-sm mt-2">
																{errors.email}
															</span>
														)}
													</div>

													<div className="col-span-6 sm:col-span-3">
														<label
															htmlFor="phoneNumber"
															className="block text-gray-500 font-medium text-sm leading-none mb-2"
														>
															Phone Number
														</label>
														<div className="relative">
															<Field
																className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg:white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
																id="phoneNumber"
																name="phoneNumber"
																placeholder="+977-9812345678"
															/>
														</div>
														{errors.phoneNumber && touched.phoneNumber && (
															<span className="text-red-400 text-sm mt-2">
																{errors.phoneNumber}
															</span>
														)}
													</div>
												</div>
											</div>
											<div className="mt-12">
												<h2 className="font-semibold  text-base text-gray-700 pb-3">
													02. Shipping Details
												</h2>
												{addresses.length > 0 && (
													<div className="mb-6 space-y-3">
														{addresses.map((addr) => (
															<label
																key={addr.id}
																className={`flex items-start gap-3 rounded-md border p-4 cursor-pointer transition-all ${selectedAddressId === addr.id
																	? 'border-brand-500 bg-brand-50'
																	: 'border-gray-200 bg-white hover:border-gray-300'
																	}`}
															>
																<input
																	type="radio"
																	name="savedAddress"
																	className="mt-1 text-brand-600 focus:ring-brand-500"
																	checked={selectedAddressId === addr.id}
																	onChange={() => setSelectedAddressId(addr.id)}
																/>
																<div className="flex-1 min-w-0">
																	<p className="text-sm font-semibold text-gray-800">
																		{addr.label || 'Saved address'}
																		{addr.is_default && (
																			<span className="ml-2 rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
																				Default
																			</span>
																		)}
																	</p>
																	<p className="text-sm text-gray-600 mt-1 break-words">
																		{addr.address}
																	</p>
																</div>
																<button
																	type="button"
																	onClick={(e) => {
																		e.preventDefault();
																		removeAddress(addr.id);
																	}}
																	className="text-xs font-medium text-gray-400 hover:text-red-500 transition-colors"
																>
																	Remove
																</button>
															</label>
														))}
														<label
															className={`flex items-center gap-3 rounded-md border p-4 cursor-pointer transition-all ${selectedAddressId === 'new'
																? 'border-brand-500 bg-brand-50'
																: 'border-gray-200 bg-white hover:border-gray-300'
																}`}
														>
															<input
																type="radio"
																name="savedAddress"
																className="text-brand-600 focus:ring-brand-500"
																checked={selectedAddressId === 'new'}
																onChange={() => setSelectedAddressId('new')}
															/>
															<span className="text-sm font-medium text-gray-700">
																Deliver to a new address
															</span>
														</label>
													</div>
												)}
												{selectedAddressId === 'new' && (
												<div className="grid grid-cols-6 gap-6 mb-8">
													<div className="col-span-4 ">
														<label
															htmlFor="address"
															className="block text-gray-500 font-medium text-sm leading-none mb-2"
														>
															Street Address
														</label>
														<div className="relative">
															<Field
																className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg:white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
																id="address"
																name="address"
																placeholder="Newroad - 12, Pokhara, Nepal"
															/>
														</div>
														{errors.address && touched.address && (
															<span className="text-red-400 text-sm mt-2">
																{errors.address}
															</span>
														)}

													</div>
													<div className="col-span-6 sm:col-span-6 lg:col-span-2">
														<label
															htmlFor="city"
															className="block text-gray-500 font-medium text-sm leading-none mb-2"
														>
															City
														</label>
														<div className="relative">
															<Field
																className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg:white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
																id="city"
																name="city"
																placeholder="Pokhara"
															/>
														</div>
														{errors.city && touched.city && (
															<span className="text-red-400 text-sm mt-2">
																{errors.city}
															</span>
														)}
													</div>
													<div className="col-span-6">
														<label className="flex items-center gap-2 cursor-pointer">
															<input
																type="checkbox"
																className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
																checked={saveNewAddress}
																onChange={(e) => setSaveNewAddress(e.target.checked)}
															/>
															<span className="text-sm text-gray-600">
																Save this address to my address book
															</span>
														</label>
														{saveNewAddress && (
															<input
																type="text"
																value={newAddressLabel}
																onChange={(e) => setNewAddressLabel(e.target.value)}
																placeholder="Label (e.g. Main Warehouse)"
																className="mt-3 py-2 px-4 w-full sm:w-1/2 appearance-none border text-sm opacity-75 rounded-md border-gray-200 focus:outline-none focus:border-brand-500 h-11"
															/>
														)}
													</div>
												</div>
												)}

											</div>

											{/* v1 ships COD only (decision on #22): the method is stated
											    explicitly instead of being silently assumed. */}
											<h2 className="font-semibold text-base text-gray-700 pb-3 pt-8">
												03. Payment
											</h2>
											<div className="rounded-md border border-brand-200 bg-brand-50 p-4">
												<div className="flex items-start">
													<span className="mt-0.5 mr-3 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 border-brand-600">
														<span className="h-2.5 w-2.5 rounded-full bg-brand-600"></span>
													</span>
													<div>
														<p className="text-sm font-semibold text-gray-800">
															Cash on Delivery
														</p>
														<p className="text-sm text-gray-600 mt-1">
															Pay in cash when your order is delivered. The total
															shown in the order summary, including the shipping
															charge, is the amount due on delivery.
														</p>
													</div>
												</div>
											</div>

											<div className="grid grid-cols-6 gap-4 lg:gap-6 mt-10">
												<div className="col-span-6 sm:col-span-3">
													<Link
														className="bg-gray-50 border !no-underline border-gray-200 rounded py-3 text-center text-sm font-medium !text-gray-700  hover:!text-gray-800 hover:border-gray-300 transition-all flex justify-center w-full"
														to="/"
													>
														<span className="text-xl mr-2">
															<svg
																stroke="currentColor"
																fill="currentColor"
																strokeWidth="0"
																viewBox="0 0 512 512"
																height="1em"
																width="1em"
																xmlns="http://www.w3.org/2000/svg"
															>
																<path
																	fill="none"
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	strokeWidth="32"
																	d="M112 160l-64 64 64 64"
																></path>
																<path
																	fill="none"
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	strokeWidth="32"
																	d="M64 224h294c58.76 0 106 49.33 106 108v20"
																></path>
															</svg>
														</span>
														Continue Shopping
													</Link>
												</div>
												<div className="col-span-6 sm:col-span-3">
													<button
														type="submit"
														disabled={isloading}
														className="bg-brand-600 hover:bg-brand-700 border border-transparent transition-all rounded py-3 text-center text-sm  font-medium text-white flex justify-center w-full"
													>
														Confirm Order{' '}
														<span className="text-xl ml-2">
															{' '}
															<svg
																stroke="currentColor"
																fill="currentColor"
																strokeWidth="0"
																viewBox="0 0 512 512"
																height="1em"
																width="1em"
																xmlns="http://www.w3.org/2000/svg"
															>
																<path
																	fill="none"
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	strokeWidth="48"
																	d="M268 112l144 144-144 144m124-144H100"
																></path>
															</svg>
														</span>
													</button>
												</div>
											</div>
										</Form>
									);
								}}

							</Formik>
						</div>
					</div>
					<OrderSummary />
				</div>
			</div>
			{/* <Payment isOpen={isPayment} setIsPayment={setIsPayment} order={order} /> */}
		</div >
	);
}

export default Checkout;
